import React, { useState, useEffect } from 'react';
import ContractSignPage from './contract-sign.jsx';

// ---------------------------------------------------------------------------
// Contractor onboarding — /onboard/:token
//
// Two steps on one public page: (1) the contractor fills in their own profile
// (contact, rates, address), (2) they review and sign the contractor
// agreement (NDA included) via the embedded signing flow.
// ---------------------------------------------------------------------------

const p = {
  cream: '#faf8f3',
  beige: '#f5f1e8',
  brown: '#3d2b1f',
  espresso: '#2c2016',
  orange: '#b8560f',
  muted: '#7a6e60',
  border: '#d9d0c3',
  red: '#a84032',
  green: '#2a5c3f',
  white: '#ffffff',
};
const mono = `'Space Mono', 'Courier Prime', monospace`;

const inputStyle = {
  width: '100%', padding: '10px 12px', fontFamily: mono, fontSize: 14,
  border: `1px solid ${p.border}`, background: p.cream, color: p.espresso,
  outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
  color: p.muted, display: 'block', marginBottom: 4,
};

export default function ContractorOnboardPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [signed, setSigned] = useState(false);
  // Rates are set by HDDN Lab on the roster, not self-reported here.
  const [form, setForm] = useState({
    name: '', phone: '', role: '', instagram: '', portfolio_url: '', address: '',
  });

  useEffect(() => {
    fetch(`/api/onboard/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'This onboarding link could not be found.');
        setData(d);
        const c = d.contractor || {};
        setForm({
          name: c.name || '',
          phone: c.phone || '',
          role: c.role || '',
          instagram: c.instagram || '',
          portfolio_url: c.portfolio_url || '',
          address: c.address || '',
        });
        if (d.contract?.status === 'signed') setSigned(true);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const update = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const saveProfile = async () => {
    if (!form.name.trim()) { alert('Your name is required.'); return; }
    setSaving(true);
    try {
      const r = await fetch(`/api/onboard/${encodeURIComponent(token)}/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Saving failed — please try again.');
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: p.cream, padding: '0 0 60px' }}>
      <style>{`
        * { box-sizing: border-box; } body { margin: 0; }
      `}</style>
      <header style={{ background: p.brown, borderBottom: `3px solid ${p.orange}`, padding: '20px 24px', textAlign: 'center' }}>
        <span style={{ fontFamily: mono, fontWeight: 700, fontSize: 22, letterSpacing: 5, color: p.cream, textTransform: 'uppercase' }}>HDDN LAB</span>
      </header>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 0' }}>{children}</div>
    </div>
  );

  if (loading) {
    return shell(<p style={{ fontFamily: mono, color: p.muted, textAlign: 'center', letterSpacing: 2 }}>LOADING...</p>);
  }
  if (error) {
    return shell(
      <div style={{ background: p.white, border: `1px solid ${p.border}`, padding: 32, textAlign: 'center' }}>
        <p style={{ fontFamily: mono, color: p.red, fontSize: 15, margin: 0 }}>{error}</p>
        <p style={{ fontFamily: mono, color: p.muted, fontSize: 12, marginTop: 12 }}>
          If you think this is a mistake, reply to the email that sent you here or contact hi@hddnlab.com.
        </p>
      </div>
    );
  }

  const hasContract = !!data.contract;
  const done = signed || (step > 2);

  const stepChip = (n, label, active, complete) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontFamily: mono, fontSize: 12, fontWeight: active ? 700 : 400,
      color: complete ? p.green : active ? p.orange : p.muted,
    }}>
      <span style={{
        width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: complete ? p.green : active ? p.orange : p.beige,
        color: complete || active ? p.white : p.muted,
        fontSize: 11, fontWeight: 700,
      }}>{complete ? '✓' : n}</span>
      {label}
    </div>
  );

  return shell(
    <>
      {/* Welcome header */}
      <div style={{ background: p.white, border: `1px solid ${p.border}`, borderTop: `4px solid ${p.orange}`, padding: '24px 28px', marginBottom: 16 }}>
        <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', color: p.orange, marginBottom: 6 }}>
          Contractor onboarding
        </div>
        <h1 style={{ fontFamily: mono, fontSize: 20, color: p.espresso, margin: '0 0 6px', letterSpacing: 1 }}>
          Welcome to the {data.provider_name} roster
        </h1>
        <p style={{ fontFamily: mono, fontSize: 12.5, color: p.muted, margin: 0, lineHeight: 1.6 }}>
          Two quick steps: confirm your details, then review and sign the contractor agreement.
        </p>
        <div style={{ display: 'flex', gap: 24, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${p.border}`, flexWrap: 'wrap' }}>
          {stepChip(1, 'Your details', step === 1 && !signed, step > 1 || signed)}
          {stepChip(2, 'Sign the agreement', step === 2 && !signed, signed)}
        </div>
      </div>

      {signed && (
        <div style={{ background: p.green, color: p.white, padding: '16px 20px', marginBottom: 16, fontFamily: mono, fontSize: 14, lineHeight: 1.6 }}>
          ✓ You're all set — welcome aboard! Your signed agreement is on its way to your inbox
          {hasContract && (
            <>
              {' '}(
              <a href={`/api/sign/${encodeURIComponent(data.contract.sign_token)}/pdf`} target="_blank" rel="noopener noreferrer" style={{ color: p.white, fontWeight: 700 }}>
                download it here
              </a>
              )
            </>
          )}. We'll be in touch about gigs.
        </div>
      )}

      {/* Step 1 — profile */}
      {step === 1 && (
        <div style={{ background: p.white, border: `1px solid ${p.border}`, padding: '24px 28px', marginBottom: 16 }}>
          <h2 style={{ fontFamily: mono, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', color: p.brown, margin: '0 0 18px' }}>
            {signed ? 'Update your details' : 'Step 1 — your details'}
          </h2>
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Full name *</label>
                <input value={form.name} onChange={(e) => update('name', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input value={form.phone} onChange={(e) => update('phone', e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Mailing address</label>
              <input value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="Street, city, state, ZIP" style={inputStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>What do you do?</label>
                <input value={form.role} onChange={(e) => update('role', e.target.value)} placeholder="Photographer, videographer, editor..." style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Instagram</label>
                <input value={form.instagram} onChange={(e) => update('instagram', e.target.value)} placeholder="@handle" style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Portfolio URL</label>
              <input value={form.portfolio_url} onChange={(e) => update('portfolio_url', e.target.value)} style={inputStyle} />
            </div>
            <button
              onClick={saveProfile}
              disabled={saving}
              style={{
                padding: '14px 28px', background: p.orange, color: p.white, border: 'none', cursor: 'pointer',
                fontFamily: mono, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                opacity: saving ? 0.6 : 1, justifySelf: 'start',
              }}
            >
              {saving ? 'Saving...' : (signed || !hasContract ? 'Save Details' : 'Continue to the Agreement →')}
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — the agreement, via the embedded signing flow */}
      {step === 2 && hasContract && (
        <>
          {!signed && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontFamily: mono, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', color: p.brown, margin: 0 }}>
                Step 2 — the agreement
              </h2>
              <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: mono, fontSize: 12, color: p.muted }}>
                ← Back to your details
              </button>
            </div>
          )}
          <ContractSignPage token={data.contract.sign_token} embedded onSigned={() => setSigned(true)} />
        </>
      )}

      {step === 2 && !hasContract && (
        <div style={{ background: p.white, border: `1px solid ${p.border}`, padding: 28, textAlign: 'center' }}>
          <p style={{ fontFamily: mono, fontSize: 13, color: p.espresso, margin: 0 }}>
            Details saved — thank you! There's no agreement waiting for your signature right now; we'll send it separately.
          </p>
        </div>
      )}

      <p style={{ fontFamily: mono, fontSize: 10, color: p.muted, textAlign: 'center', marginTop: 24, letterSpacing: 1 }}>
        HDDN LAB · 72 Centre Avenue, New Rochelle, NY · hi@hddnlab.com
      </p>
    </>
  );
}
